import type { IconComponentName } from "@/shared/icons";

export const STEP_SHAPE_HEIGHT = 58;
export const STEP_SHAPE_FALLBACK_WIDTH = 420;

const STEP_SHAPE_ARROW_WIDTH = 24;
const STEP_SHAPE_RADIUS = 8;
const STEP_SHAPE_MIN_WIDTH = 120;

const getSafeStepShapeWidth = (width = STEP_SHAPE_FALLBACK_WIDTH) =>
  Math.max(Math.round(width), STEP_SHAPE_MIN_WIDTH);

const roundedRectPath = (width: number) => {
  const w = getSafeStepShapeWidth(width);
  const r = STEP_SHAPE_RADIUS;
  const y0 = 1;
  const y1 = STEP_SHAPE_HEIGHT - 1;

  return `M${r} ${y0}H${w - r}C${w - 3.5} ${y0} ${w - 1} 3.5 ${w - 1} ${r}V${y1 - r}C${w - 1} ${y1 - 3.5} ${w - 3.5} ${y1} ${w - r} ${y1}H${r}C3.5 ${y1} 1 ${y1 - 3.5} 1 ${y1 - r}V${r}C1 3.5 3.5 ${y0} ${r} ${y0}Z`;
};

const rightArrowSegment = (width: number) => {
  const w = getSafeStepShapeWidth(width);
  const arrowBase = w - STEP_SHAPE_ARROW_WIDTH;
  const tipX = w - 1;
  const mid = STEP_SHAPE_HEIGHT / 2;

  return `H${arrowBase}C${arrowBase + 3.8} 1 ${arrowBase + 6.4} 2.7 ${arrowBase + 8.4} 6.3L${tipX - 1.3} ${mid - 2.8}C${tipX - 0.2} ${mid - 1.3} ${tipX - 0.2} ${mid + 1.3} ${tipX - 1.3} ${mid + 2.8}L${arrowBase + 8.4} ${STEP_SHAPE_HEIGHT - 6.3}C${arrowBase + 6.4} ${STEP_SHAPE_HEIGHT - 2.7} ${arrowBase + 3.8} ${STEP_SHAPE_HEIGHT - 1} ${arrowBase} ${STEP_SHAPE_HEIGHT - 1}`;
};

const rightRoundedSegment = (width: number) => {
  const w = getSafeStepShapeWidth(width);
  const r = STEP_SHAPE_RADIUS;
  const y0 = 1;
  const y1 = STEP_SHAPE_HEIGHT - 1;

  return `H${w - r}C${w - 3.5} ${y0} ${w - 1} 3.5 ${w - 1} ${r}V${y1 - r}C${w - 1} ${y1 - 3.5} ${w - 3.5} ${y1} ${w - r} ${y1}`;
};

const leftRoundedSegment = () => {
  const r = STEP_SHAPE_RADIUS;
  const y1 = STEP_SHAPE_HEIGHT - 1;

  return `H${r}C3.5 ${y1} 1 ${y1 - 3.5} 1 ${y1 - r}V${r}C1 3.5 3.5 1 ${r} 1Z`;
};

const leftNotchSegment = () => {
  const mid = STEP_SHAPE_HEIGHT / 2;

  return `H8C4 ${STEP_SHAPE_HEIGHT - 1} 1.5 ${STEP_SHAPE_HEIGHT - 4.6} 4.8 ${STEP_SHAPE_HEIGHT - 7.8}L16.8 ${mid + 4.8}C19.6 ${mid + 2} 19.6 ${mid - 2} 16.8 ${mid - 4.8}L4.8 7.8C1.5 4.6 4 1 8 1Z`;
};

const firstStepPath = (width: number) =>
  `M8 1${rightArrowSegment(width)}${leftRoundedSegment()}`;

const middleStepPath = (width: number) =>
  `M8 1${rightArrowSegment(width)}${leftNotchSegment()}`;

const lastStepPath = (width: number) =>
  `M8 1${rightRoundedSegment(width)}${leftNotchSegment()}`;

export const getStepShapePath = (
  index: number,
  totalSteps: number,
  width = STEP_SHAPE_FALLBACK_WIDTH,
) => {
  if (totalSteps <= 1) {
    return roundedRectPath(width);
  }

  if (index === 0) {
    return firstStepPath(width);
  }

  if (index === totalSteps - 1) {
    return lastStepPath(width);
  }

  return middleStepPath(width);
};

interface GetStepDisplayIconParams {
  icon: IconComponentName;
  completedIcon?: IconComponentName;
  editableIcon?: IconComponentName;
  isActive: boolean;
  isCompleted: boolean;
  isHovered: boolean;
}

export const getStepDisplayIcon = ({
  icon,
  completedIcon = "iconCheckCircle",
  editableIcon = "iconPencil",
  isActive,
  isCompleted,
  isHovered,
}: GetStepDisplayIconParams): IconComponentName => {
  if (isActive || !isCompleted) {
    return icon;
  }

  return isHovered ? editableIcon : completedIcon;
};

export const hasFilledFormFlowValue = (value: unknown): boolean => {
  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.some(hasFilledFormFlowValue);
  }

  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).some(
      hasFilledFormFlowValue,
    );
  }

  return false;
};
